import React from 'react';
import styled from 'styled-components';
import api from '../api/axiosConfig';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const Page = styled.div`
  width: min(100%, 1800px);
  margin: 18px auto;
  padding: 18px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 16px 46px rgba(0, 0, 0, 0.18);

  @media (max-width: 560px) {
    margin: 10px auto;
    padding: 14px;
    border-radius: 22px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-weight: 950;
  color: #053969;
`;

const Hello = styled.div`
  margin-top: 8px;
  font-weight: 900;
  color: rgba(5, 57, 105, 0.88);
`;

const Banner = styled.div`
  margin-top: 12px;
  border-radius: 18px;
  padding: 12px 14px;
  border: 1px solid rgba(220, 38, 38, 0.25);
  background: rgba(220, 38, 38, 0.08);
  color: rgba(0, 0, 0, 0.78);
  font-weight: 800;
`;

const Section = styled.section`
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-weight: 950;
  color: #053969;
  font-size: 22px;
`;

const Row = styled.div`
  display: grid;
  gap: 10px;
  @media (min-width: 860px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.div`
  border-radius: 18px;
  padding: 14px;
  background: rgba(5, 57, 105, 0.04);
  border: 1px solid rgba(5, 57, 105, 0.12);
`;

const Label = styled.div`
  font-size: 12px;
  opacity: 0.7;
  margin-bottom: 6px;
`;

const Value = styled.div`
  font-weight: 900;
  color: #0b0b0b;
  overflow-wrap: anywhere;
`;

const StatusPill = styled.span`
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: ${(p) => p.$bg || 'rgba(255,255,255,0.7)'};
  color: ${(p) => p.$color || '#0b0b0b'};
`;

const Form = styled.form`
  display: grid;
  gap: 12px;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  border: 1px solid rgba(5, 57, 105, 0.18);
  background: rgba(255, 255, 255, 0.85);
  border-radius: 14px;
  padding: 12px 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: rgba(5, 57, 105, 0.45);
    box-shadow: 0 0 0 4px rgba(5, 57, 105, 0.12);
  }
`;

const Textarea = styled.textarea`
  border: 1px solid rgba(5, 57, 105, 0.18);
  background: rgba(255, 255, 255, 0.85);
  border-radius: 14px;
  padding: 12px 12px;
  font-size: 14px;
  outline: none;
  resize: vertical;
  min-height: 90px;

  &:focus {
    border-color: rgba(5, 57, 105, 0.45);
    box-shadow: 0 0 0 4px rgba(5, 57, 105, 0.12);
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

 @media (max-width: 560px) {
  width: 100%;
 }
`;

const Btn = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.16);
  background: #fff;
  color: #0b0b0b;
  border-radius: 999px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: 900;
  transition: transform 160ms ease;
  min-height: 44px;
  min-width: 140px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.2;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

 @media (max-width: 560px) {
  width: 100%;
  min-width: 0;
  white-space: normal;
 }
`;

const Danger = styled.div`
  border-radius: 18px;
  padding: 12px 14px;
  border: 1px solid rgba(220, 38, 38, 0.25);
  background: rgba(220, 38, 38, 0.08);
  color: rgba(0, 0, 0, 0.78);
`;

const Small = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 6px;
`;

const ChatBox = styled.div`
  margin-top: 12px;
  padding: 16px;
  border-radius: 20px;
  background:
    radial-gradient(circle at top right, rgba(183, 255, 83, 0.18), transparent 34%),
    linear-gradient(145deg, rgba(5, 57, 105, 0.96), rgba(8, 34, 58, 0.94));
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 14px 34px rgba(5, 57, 105, 0.18);
`;

const ChatMessages = styled.div`
  max-height: 320px;
  overflow-y: auto;
  display: grid;
  gap: 10px;
  padding: 2px 2px 8px;
`;

const ChatBubble = styled.div`
  justify-self: ${(p) => (p.$mine ? 'end' : 'start')};
  max-width: min(82%, 760px);
  padding: 11px 13px;
  border-radius: 16px;
  border: 1px solid ${(p) => (p.$mine ? 'rgba(183,255,83,0.34)' : 'rgba(255,255,255,0.18)')};
  background: ${(p) => (p.$mine ? 'rgba(183,255,83,0.92)' : 'rgba(255,255,255,0.96)')};
  color: #0b0b0b;
  font-weight: 800;
  line-height: 1.45;
  overflow-wrap: anywhere;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);

  @media (max-width: 560px) {
    max-width: 92%;
  }
`;

const ChatTime = styled.div`
  font-size: 11px;
  opacity: 0.58;
  margin-top: 5px;
  font-weight: 700;
`;

const ChatComposer = styled.form`
  margin-top: 12px;
  display: flex;
  align-items: flex-end;
  gap: 10px;

  @media (max-width: 560px) {
    display: grid;
    grid-template-columns: 1fr;
    justify-items: start;
  }
`;

const ChatTextarea = styled.textarea`
  flex: 1;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  min-height: 96px;
  max-height: 220px;
  resize: vertical;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.97);
  color: #0b0b0b;
  border-radius: 15px;
  padding: 12px 14px;
  font: inherit;
  line-height: 1.4;
  outline: none;

  &:focus {
    border-color: rgba(183, 255, 83, 0.8);
    box-shadow: 0 0 0 4px rgba(183, 255, 83, 0.16);
  }

  @media (min-width: 900px) {
    min-height: 112px;
  }

  @media (max-width: 560px) {
    min-height: 110px;
  }
`;

const ChatSendButton = styled.button`
  border: 1px solid rgba(183, 255, 83, 0.72);
  background: #b7ff53;
  color: #09233a;
  border-radius: 999px;
  padding: 9px 16px;
  min-height: 40px;
  width: auto;
  cursor: pointer;
  font-weight: 950;
  white-space: nowrap;
  transition: transform 160ms ease, box-shadow 160ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(183, 255, 83, 0.2);
  }
`;


const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: ${(p) => p.$c || 'rgba(0,0,0,0.4)'};
  display: inline-block;
  margin-right: 8px;
`;

export default function UserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [me, setMe] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const [first, setFirst] = React.useState('');
  const [last, setLast] = React.useState('');
  const [age, setAge] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [nativeLang, setNativeLang] = React.useState('');

  const [whatsapp, setWhatsapp] = React.useState('');
  const [telegram, setTelegram] = React.useState('');
  const [viber, setViber] = React.useState('');
  const [messengerPhone, setMessengerPhone] = React.useState('');

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');

  const [orders, setOrders] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(false);

  const [delReq, setDelReq] = React.useState(null);
  const [delReason, setDelReason] = React.useState('');
  const [delLoading, setDelLoading] = React.useState(false);

  const [chat, setChat] = React.useState(null);
  const [chatLoading, setChatLoading] = React.useState(false);
  const [chatText, setChatText] = React.useState('');

  const loadMe = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/me/');
      setMe(res.data);

      setFirst(res.data.first_name || '');
      setLast(res.data.last_name || '');
      setAge(res.data.age || '');
      setPhone(res.data.phone || '');
      setCountry(res.data.country || '');
      setNativeLang(res.data.native_language || '');

      setWhatsapp(res.data.whatsapp || '');
      setTelegram(res.data.telegram || '');
      setViber(res.data.viber || '');
      setMessengerPhone(res.data.messenger_phone || '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDeleteRequest = React.useCallback(async () => {
    try {
      const res = await api.get('/me/delete-request/');
      setDelReq(res.data);
    } catch {
      setDelReq(null);
    }
  }, []);

  const loadChat = React.useCallback(async () => {
    setChatLoading(true);
    try {
      const res = await api.get('/chat/');
      setChat(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setChatLoading(false);
    }
  }, []);

  const loadOrders = React.useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await api.get('/orders/my/');
      setOrders(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadMe();
  }, [loadMe]);

  React.useEffect(() => {
    if (!loading && me) {
      loadOrders();
      loadChat();
      loadDeleteRequest();
    }
  }, [loading, me, loadOrders, loadChat, loadDeleteRequest]);

  const isProfileComplete = (data) => {
  return (
    String(data.first_name || '').trim().length >= 2 &&
    String(data.last_name || '').trim().length >= 2 &&
    Number(data.age) >= 14 &&
    String(data.phone || '').trim().length >= 6 &&
    String(data.country || '').trim().length >= 2 &&
    String(data.native_language || '').trim().length >= 2
  );
};

  const statusUI = (m) => {
    if (!m)
      return {
        text: t('user.status.under_review'),
        bg: 'rgba(255,255,255,0.7)',
        color: '#0b0b0b',
      };

    if (m.is_banned) {
      return {
        text: t('user.status.banned'),
        bg: 'rgba(220,38,38,0.12)',
        color: 'rgba(150,0,0,0.95)',
      };
    }

    if (m.verification_status === 'verified') {
      return { text: t('user.status.verified'), bg: 'rgba(183,255,83,0.55)', color: '#0b0b0b' };
    }

    if (m.verification_status === 'under_review') {
      return { text: t('user.status.under_review'), bg: 'rgba(255,255,255,0.7)', color: '#0b0b0b' };
    }

    if (m.verification_status === 'rejected') {
      return {
        text: t('user.status.rejected'),
        bg: 'rgba(220,38,38,0.10)',
        color: 'rgba(150,0,0,0.95)',
      };
    }

    return {
      text: t('user.status.not_verified'),
      bg: 'rgba(220,38,38,0.10)',
      color: 'rgba(150,0,0,0.95)',
    };
  };

  const orderStatusUI = (status) => {
    if (status === 'new')
      return { label: t('user.orders.status.pending'), dot: 'rgba(255, 196, 0, 0.95)' };
    if (status === 'in_progress')
      return { label: t('user.orders.status.confirmed'), dot: 'rgba(5, 57, 105, 0.95)' };
    if (status === 'done')
      return { label: t('user.orders.status.completed'), dot: 'rgba(34, 197, 94, 0.95)' };
    if (status === 'cancel_requested')
      return { label: t('user.orders.status.cancel_requested'), dot: 'rgba(220, 38, 38, 0.95)' };
    if (status === 'cancelled')
      return { label: t('user.orders.status.cancelled'), dot: 'rgba(120,120,120,0.95)' };
    return { label: status, dot: 'rgba(0,0,0,0.5)' };
  };

  const onlyLetters = (value) =>
  value.replace(/[^A-Za-zА-Яа-яЁёІіЇїЄєҐґ\s-]/g, '');

  const onlyPhone = (value) =>
  value.replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');

  const onlyAge = (value) =>
  value.replace(/\D/g, '').slice(0, 3);

  const onlyTelegram = (value) =>
  value.replace(/[^A-Za-z0-9_@]/g, '').replace(/(?!^)@/g, '');
  
  const saveProfile = async (e) => {
    e.preventDefault();
    setSaveError('');

    try {
      setSaving(true);

      const fd = new FormData();
      fd.append('first_name', first);
      fd.append('last_name', last);
      fd.append('age', age);
      fd.append('phone', phone);
      fd.append('country', country);
      fd.append('native_language', nativeLang);

      fd.append('whatsapp', whatsapp);
      fd.append('telegram', telegram);
      fd.append('viber', viber);
      fd.append('messenger_phone', messengerPhone);

      await api.patch('/me/profile/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      await loadMe();
      alert(t('user.alerts.saved'));
    } catch (e2) {
      console.error(e2);
      const msg =
        e2?.response?.data?.detail ||
        (e2?.response?.data?.missing
          ? t('user.errors.missing', { fields: e2.response.data.missing.join(', ') })
          : null) ||
        t('user.errors.save_failed');
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = async () => {
    try {
      setDelLoading(true);
      const res = await api.post('/me/delete-request/', { reason: delReason });
      setDelReq(res.data);
      alert(t('user.alerts.delete_sent'));
    } catch (e) {
      console.error(e);
      alert(t('user.alerts.delete_failed'));
    } finally {
      setDelLoading(false);
    }
  };

  const sendChat = async (e) => {
    e.preventDefault();
    const txt = chatText.trim();
    if (!txt) return;

    try {
      await api.post('/chat/', { text: txt });
      setChatText('');
      await loadChat();
    } catch (e) {
      console.error(e);
      alert(t('user.alerts.chat_failed'));
    }
  };

  if (loading) return <Page>{t('common.loading')}</Page>;

  const helloName = me?.first_name?.trim() ? me.first_name : me?.email || t('user.fallback_user');
  const profOk = isProfileComplete({
  first_name: first,
  last_name: last,
  age,
  phone,
  country,
  native_language: nativeLang,
});
  const s = statusUI(me);

  return (
    <Page>
      <Title>{t('user.title')}</Title>
      <Hello>{t('user.hello', { name: helloName })}</Hello>

      {!profOk ? <Banner>{t('user.banner.incomplete')}</Banner> : null}

      {me?.is_banned ? (
        <Section>
          <Danger>
            <strong>{t('user.banner.banned_title')}</strong>
            {me?.ban_reason ? <div style={{ marginTop: 6 }}>{me.ban_reason}</div> : null}
          </Danger>
        </Section>
      ) : null}

      <Section>
        <Row>
          <Card>
            <Label>{t('user.account.status')}</Label>
            <Value>
              <StatusPill $bg={s.bg} $color={s.color}>
                {s.text}
              </StatusPill>
            </Value>
          </Card>

          <Card>
            <Label>{t('user.account.registered')}</Label>
            <Value>{me?.date_joined ? new Date(me.date_joined).toLocaleDateString() : '—'}</Value>
            <Small>{me?.email || ''}</Small>
          </Card>
        </Row>
      </Section>

      <Section>
        <SectionTitle>{t('user.profile.title')}</SectionTitle>

        {me?.profile_locked ? (
          <Banner style={{ borderColor: 'rgba(5,57,105,0.25)', background: 'rgba(5,57,105,0.06)' }}>
            {t('user.profile.locked_note')}
          </Banner>
        ) : null}

        <Form onSubmit={saveProfile} style={{ marginTop: 12 }}>
          <Grid2>
            <div>
              <Label>{t('user.profile.first_name')}</Label>
              <Input
                value={first}
                onChange={(e) => setFirst(onlyLetters(e.target.value))}
                placeholder="Name"
                disabled={!!me?.profile_locked}
              />
            </div>
            <div>
              <Label>{t('user.profile.last_name')}</Label>
              <Input
                value={last}
                onChange={(e) => setLast(onlyLetters(e.target.value))}
                placeholder="Surname"
                disabled={!!me?.profile_locked}
              />
            </div>
          </Grid2>

          <Grid2>
            <div>
              <Label>{t('user.profile.age')}</Label>
              <Input
                type="number"
                min="14"
                max="120"
                value={age}
                onChange={(e) => setAge(onlyAge(e.target.value))}
                placeholder="Age"
                disabled={!!me?.profile_locked}
              />
            </div>
            <div>
              <Label>{t('user.profile.phone')}</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(onlyPhone(e.target.value))}
                placeholder="PhoneNumber"
                disabled={!!me?.profile_locked}
              />
            </div>
          </Grid2>

          <Grid2>
            <div>
              <Label>{t('user.profile.country')}</Label>
              <Input
                value={country}
                onChange={(e) => setCountry(onlyLetters(e.target.value))}
                placeholder="Country"
                disabled={!!me?.profile_locked}
              />
            </div>
            <div>
              <Label>{t('user.profile.native_language')}</Label>
              <Input
                value={nativeLang}
                onChange={(e) => setNativeLang(onlyLetters(e.target.value))}
                placeholder="YourCountry"
                disabled={!!me?.profile_locked}
              />
            </div>
          </Grid2>

          <Card>
            <Label>{t('user.messengers.title')}</Label>
            <Grid2>
              <div>
                <Label>{t('user.messengers.whatsapp')}</Label>
                <Input
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(onlyPhone(e.target.value))}
                  placeholder="WhatsApp"
                />
              </div>
              <div>
                <Label>{t('user.messengers.telegram')}</Label>
                <Input
                  value={telegram}
                  onChange={(e) => setTelegram(onlyTelegram(e.target.value))}
                  placeholder="@username"
                />
              </div>
            </Grid2>
            <Grid2 style={{ marginTop: 10 }}>
              <div>
                <Label>{t('user.messengers.viber')}</Label>
                <Input
                  value={viber}
                  onChange={(e) => setViber(onlyPhone(e.target.value))}
                  placeholder="Viber"
                />
              </div>
              <div>
                <Label>{t('user.messengers.other')}</Label>
                <Input
                  value={messengerPhone}
                  onChange={(e) => setMessengerPhone(onlyPhone(e.target.value))}
                  placeholder="PhoneNumber"
                />
              </div>
            </Grid2>
          </Card>

          {saveError ? (
            <Banner style={{ borderColor: 'rgba(220,38,38,0.35)' }}>{saveError}</Banner>
          ) : null}
          <BtnRow>
            <Btn type="submit" disabled={saving}>
  {saving ? (t('common.saving') || 'Saving...') : (t('user.profile.save') || 'Save profile')}
</Btn>
          </BtnRow>

          <Small>{t('user.profile.after_save')}</Small>
        </Form>
      </Section>

      <Section>
        <SectionTitle>{t('user.orders.title')}</SectionTitle>

        {ordersLoading ? (
          <div style={{ marginTop: 10 }}>{t('user.orders.loading')}</div>
        ) : orders.length === 0 ? (
          <div style={{ marginTop: 10, opacity: 0.75 }}>{t('user.orders.empty')}</div>
        ) : (
          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            {orders.map((o) => {
              const st = orderStatusUI(o.status);
              return (
                <Card key={o.id}>
                  <Row>
                    <div>
                      <Label>{t('user.orders.order')}</Label>
                      <Value>
                        #{o.order_number || o.id} {o.order_name || ''}
                      </Value>
                    </div>

                    <div>
                      <Label>{t('user.orders.status_label')}</Label>
                      <Value style={{ display: 'flex', alignItems: 'center' }}>
                        <Dot $c={st.dot} />
                        {st.label}
                      </Value>
                    </div>
                  </Row>

                  {o.status === 'awaiting_payment' ? (
                    <div style={{ marginTop: 10 }}>
                      <Btn
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await api.post(`/payments/paypal/start/${o.id}/`);
                            const approvalUrl = res?.data?.approval_url;

                            if (!approvalUrl) {
                              throw new Error('Missing PayPal approval URL');
                            }

                            window.location.assign(approvalUrl);
                          } catch (e) {
                            console.error(e);
                            alert('Unable to start PayPal payment.');
                          }
                        }}
                      >
                        Pay with PayPal
                      </Btn>
                    </div>
                  ) : null}

                  {o.download_available && o.download_url ? (
                    <div style={{ marginTop: 10 }}>
                      <Btn
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await api.post(
                              `/licenses/${o.id}/download-link/`
                            );

                            const downloadUrl = res.data?.download_url;

                            if (!downloadUrl) {
                              throw new Error('Missing download URL');
                            }

                            window.location.assign(downloadUrl);
                          } catch (e) {
                            console.error(e);
                            alert('Unable to download package.');
                          }
                        }}
                      >
                        Download package
                      </Btn>
                    </div>
                  ) : null}

                  {o.download_available ? (
                    <div style={{ marginTop: 10 }}>
                      <Btn
                        type="button"
                        onClick={() => navigate(`/autoinstall/${o.id}`)}
                      >
                        AutoInstall
                      </Btn>
                    </div>
                  ) : null}

                  {o.status !== 'done' && o.status !== 'cancelled' ? (
                    <div style={{ marginTop: 10 }}>
                      <Btn
                        type="button"
                        onClick={async () => {
                          try {
                            await api.post(`/orders/${o.id}/cancel/`);
                            await loadOrders();
                            alert(t('user.alerts.cancel_requested'));
                          } catch {
                            alert(t('user.alerts.cancel_failed'));
                          }
                        }}
                      >
                        {t('user.orders.request_cancel')}
                      </Btn>
                    </div>
                  ) : null}
                </Card>
              );
            })}
          </div>
        )}
      </Section>

      <Section>
        <SectionTitle>{t('user.chat.title')}</SectionTitle>

        {chatLoading ? (
          <div style={{ marginTop: 10 }}>{t('user.chat.loading')}</div>
        ) : (
          <>
            {/* OLD CHAT UI saved in UserPage.js.before-chat-ui */}
            <ChatBox>
            <ChatMessages>
              {(chat?.messages || []).map((m) => (
                <ChatBubble key={m.id} $mine={m.is_mine}>
                  {m.text}
                  <ChatTime>
                    {m.created_at ? new Date(m.created_at).toLocaleString() : ''}
                  </ChatTime>
                </ChatBubble>
              ))}

              {!chat?.messages || chat.messages.length === 0 ? (
                <div style={{ color: 'rgba(255,255,255,0.78)', fontWeight: 700 }}>
                  {t('user.chat.empty')}
                </div>
              ) : null}
            </ChatMessages>

            <ChatComposer onSubmit={sendChat}>
              <ChatTextarea
                value={chatText}
                onChange={(e) => setChatText(e.target.value)}
                placeholder={t('user.chat.placeholder')}
              />
              <ChatSendButton type="submit">{t('user.chat.send')}</ChatSendButton>
            </ChatComposer>

            <Small style={{ marginTop: 8, color: 'rgba(255,255,255,0.74)' }}>
              {t('user.chat.note')}
            </Small>
            </ChatBox>
          </>
        )}
      </Section>

      <Section>
        <SectionTitle>{t('user.delete.title')}</SectionTitle>

        <Card style={{ marginTop: 12 }}>
          {delReq?.is_approved ? (
            <Banner
              style={{
                borderColor: 'rgba(34,197,94,0.35)',
                background: 'rgba(34,197,94,0.08)',
              }}
            >
              {t('user.delete.approved')}
            </Banner>
          ) : delReq ? (
            <Banner>
              {t('user.delete.sent_at')}{' '}
              {delReq.requested_at ? new Date(delReq.requested_at).toLocaleString() : ''}.{' '}
              {t('user.delete.waiting')}
            </Banner>
          ) : (
            <>
              <Label>{t('user.delete.reason')}</Label>
              <Textarea
                value={delReason}
                onChange={(e) => setDelReason(e.target.value)}
                placeholder={t('user.delete.reason_ph')}
              />
              <div style={{ marginTop: 10 }}>
                <Btn type="button" disabled={delLoading} onClick={requestDelete}>
                  {delLoading ? t('common.sending') : t('user.delete.request')}
                </Btn>
              </div>
              <Small>{t('user.delete.note')}</Small>
            </>
          )}
        </Card>
      </Section>
    </Page>
  );
}
